import { useState, useEffect } from 'react';
import { Star, Save, Check, AlertCircle, Info, Send, ExternalLink, MessageCircle, Mail } from '../../icons/wp-icons';
import apiClient from '../../api/apiClient';

const DEFAULT_TEMPLATE = 'Hallo {{kunde_name}}, vielen Dank für Ihr Vertrauen! Falls Sie zufrieden waren, freuen wir uns sehr über eine kurze Google-Bewertung: {{review_link}}';

function GoogleReviewSettings({ readOnly = false }) {
  // Einstellungen
  const [enabled, setEnabled] = useState(false);
  const [placeId, setPlaceId] = useState('');
  const [delayDays, setDelayDays] = useState(2);
  const [channel, setChannel] = useState('whatsapp');
  const [followupEnabled, setFollowupEnabled] = useState(false);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [whatsappVerfuegbar, setWhatsappVerfuegbar] = useState(false);

  // Statistiken
  const [statistiken, setStatistiken] = useState(null);

  // UI State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);

      const settingsRes = await apiClient.get('/google-reviews/einstellungen');
      const settings = settingsRes.data;

      setEnabled(settings.enabled || false);
      setPlaceId(settings.placeId || '');
      setDelayDays(settings.delayDays || 2);
      setChannel(settings.channel || 'whatsapp');
      setFollowupEnabled(settings.followupEnabled || false);
      setMessageTemplate(settings.messageTemplate || DEFAULT_TEMPLATE);
      setWhatsappVerfuegbar(settings.whatsappVerfuegbar || false);

      try {
        const statsRes = await apiClient.get('/google-reviews/statistiken');
        setStatistiken(statsRes.data);
      } catch (_) { /* stats optional */ }

    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der Google-Review-Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      await apiClient.post('/google-reviews/einstellungen', {
        enabled,
        placeId,
        delayDays,
        channel,
        followupEnabled,
        messageTemplate
      });

      // Statistiken neu laden
      try {
        const statsRes = await apiClient.get('/google-reviews/statistiken');
        setStatistiken(statsRes.data);
      } catch (_) { /* ignore */ }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const reviewLink = placeId
    ? `https://search.google.com/local/writereview?placeid=${placeId}`
    : '';

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Star className="w-6 h-6 text-yellow-500" />
          <div>
            <h2 className="text-lg font-semibold">Google Bewertungen</h2>
            <p className="text-sm text-gray-500">
              Automatisch Kunden nach Auftragsabschluss um Google-Bewertungen bitten
            </p>
          </div>
        </div>

        {/* Statistik-Badge */}
        {statistiken && statistiken.gesamt > 0 && (
          <div className="hidden sm:flex items-center gap-2 bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-full text-sm font-medium">
            <Star className="w-4 h-4" />
            {statistiken.gesamt} Anfragen gesamt
          </div>
        )}
      </div>

      {/* Aktivierungs-Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-6">
        <div>
          <p className="font-medium">Google Reviews aktivieren</p>
          <p className="text-sm text-gray-500">Automatisch Bewertungsanfragen senden</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={readOnly}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
        </label>
      </div>

      {enabled && (
        <div className="space-y-6">
          {/* Google Place ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Google Place ID
            </label>
            <input
              type="text"
              value={placeId}
              onChange={(e) => setPlaceId(e.target.value)}
              disabled={readOnly}
              placeholder="z.B. ChIJN1t_tDeuEmsRUsoyG83frY4"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100"
            />
            <div className="mt-1.5 flex items-start gap-1.5">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-500">
                Suchen Sie Ihren Betrieb auf Google Maps, klicken Sie auf "Teilen" und kopieren Sie die Place ID aus der URL.
                Alternativ: <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" className="text-primary underline">Place ID Finder</a>
              </p>
            </div>

            {/* Link-Vorschau */}
            {reviewLink && (
              <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs font-medium text-green-700 mb-1">Bewertungslink:</p>
                <a
                  href={reviewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-green-600 underline break-all flex items-center gap-1"
                >
                  {reviewLink}
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </div>
            )}
          </div>

          {/* Verzögerung */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Verzögerung nach Abschluss
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="1"
                max="14"
                value={delayDays}
                onChange={(e) => setDelayDays(parseInt(e.target.value))}
                disabled={readOnly}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <span className="text-sm font-medium text-gray-700 min-w-[60px] text-right">
                {delayDays} {delayDays === 1 ? 'Tag' : 'Tage'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Wie viele Tage nach Auftragsabschluss oder Rechnungsbezahlung soll die Anfrage gesendet werden?
            </p>
          </div>

          {/* Kanal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Versandkanal
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, disabled: !whatsappVerfuegbar },
                { value: 'email', label: 'E-Mail', icon: Mail, disabled: false },
                { value: 'both', label: 'Beide', icon: Send, disabled: !whatsappVerfuegbar }
              ].map(({ value, label, icon: Icon, disabled }) => (
                <button
                  key={value}
                  onClick={() => !readOnly && !disabled && setChannel(value)}
                  disabled={readOnly || disabled}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 transition text-sm font-medium ${
                    channel === value
                      ? 'border-primary bg-primary/5 text-primary'
                      : disabled
                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  {disabled && <span className="text-xs">(nicht verbunden)</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Nachrichtentemplate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nachrichtentext
            </label>
            <textarea
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              disabled={readOnly}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 resize-y"
              placeholder={DEFAULT_TEMPLATE}
            />
            <div className="mt-1.5 flex flex-wrap gap-2">
              {['{{kunde_name}}', '{{betrieb_name}}', '{{review_link}}'].map(ph => (
                <span key={ph} className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-mono">
                  {ph}
                </span>
              ))}
            </div>
          </div>

          {/* Follow-up Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-sm">Follow-up aktivieren</p>
              <p className="text-xs text-gray-500">Erinnerung 5 Tage nach der ersten Anfrage senden</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={followupEnabled}
                onChange={(e) => setFollowupEnabled(e.target.checked)}
                disabled={readOnly}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {/* Statistiken */}
          {statistiken && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Gesamt gesendet', value: statistiken.gesamt },
                { label: 'Letzte 30 Tage', value: statistiken.letzteMonat },
                { label: 'Follow-ups', value: statistiken.followupGesendet },
                { label: 'Aktuell fällig', value: statistiken.naechsteFaellige }
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-800">{value}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Anti-Spam Hinweis */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              <strong>Anti-Spam:</strong> Jeder Kunde erhält maximal eine Bewertungsanfrage alle 6 Monate.
              Die Anfrage wird nur gesendet, wenn eine Telefonnummer (WhatsApp) oder E-Mail-Adresse hinterlegt ist.
            </p>
          </div>
        </div>
      )}

      {/* Fehlermeldung */}
      {error && (
        <div className="mt-4 flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Speichern Button */}
      {!readOnly && (
        <div className="pt-4 border-t border-border">
          {saved && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              <Check className="w-4 h-4" />
              Einstellungen gespeichert
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Einstellungen speichern
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default GoogleReviewSettings;
